import { CONTRACT_FILTERABLE_FIELDS } from '@vy/contracts-types';
import { CONTRACT_STATUSES, CURRENCIES, MONGO_FILTER_OPERATORS, SYNC_STATUSES } from '@vy/types';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import { CONTRACTS_COLUMNS_KEYS } from '../plans-page-consts/contracts-columns';

const OPERATORS_BY_TYPE = {
  text: [{ label: 'Contains', value: MONGO_FILTER_OPERATORS.REGEX }],
  number: [
    { label: 'Equals', value: MONGO_FILTER_OPERATORS.EQUALS },
    { label: 'Greater Than', value: MONGO_FILTER_OPERATORS.GREATER_THAN },
    { label: 'Less than', value: MONGO_FILTER_OPERATORS.LESS_THAN },
    { label: 'Greater than or equal', value: MONGO_FILTER_OPERATORS.GREATER_THAN_OR_EQUAL },
    { label: 'Less than or equal', value: MONGO_FILTER_OPERATORS.LESS_THAN_OR_EQUAL },
  ],
  date: [
    { label: 'Is on', value: MONGO_FILTER_OPERATORS.EQUALS },
    { label: 'Is after', value: MONGO_FILTER_OPERATORS.GREATER_THAN },
    { label: 'Is before', value: MONGO_FILTER_OPERATORS.LESS_THAN },
  ],
  select: [
    { label: 'Is any of', value: MONGO_FILTER_OPERATORS.IN },
    { label: 'Is not any of', value: MONGO_FILTER_OPERATORS.NOT_IN },
  ],
  boolean: [{ label: 'Is', value: MONGO_FILTER_OPERATORS.EQUALS }],
};

const getOperatorsByInputType = (inputType: keyof typeof OPERATORS_BY_TYPE) =>
  OPERATORS_BY_TYPE[inputType] ?? [];

const createFilterField = (
  id: string,
  label: string,
  inputType: keyof typeof OPERATORS_BY_TYPE,
  options?: { value: string; label: string }[],
): FilterField => ({
  id,
  label,
  inputType,
  operators: getOperatorsByInputType(inputType),
  ...(options && { options }),
});

const createEnumSelectField = (
  id: string,
  label: string,
  enumObject: Record<string, string | number>,
  labelMapper?: (key: string) => string,
  valueMapper?: (value: string | number) => string,
): FilterField => {
  const options = Object.entries(enumObject).map(([key, value]) => ({
    value: valueMapper ? valueMapper(value) : String(value),
    label: labelMapper ? labelMapper(key) : key,
  }));

  return createFilterField(id, label, 'select', options);
};

const createBooleanField = (id: string, label: string): FilterField =>
  createFilterField(id, label, 'boolean', [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ]);

export const CONTRACTS_FILTER_FIELDS: FilterField[] = [
  createEnumSelectField(CONTRACTS_COLUMNS_KEYS.STATUS, 'Status', CONTRACT_STATUSES),
  createFilterField(CONTRACTS_COLUMNS_KEYS.CUSTOMER_NAME, 'Customer Name', 'text'),
  createFilterField(CONTRACTS_COLUMNS_KEYS.PLAN_NAME, 'Contract Name', 'text'),
  createFilterField(CONTRACTS_COLUMNS_KEYS.START_DATE, 'Start Date', 'date'),
  createFilterField(CONTRACTS_COLUMNS_KEYS.END_DATE, 'End Date', 'date'),
  createFilterField(CONTRACTS_COLUMNS_KEYS.CREATED_AT, 'Created Date', 'date'),
  createFilterField(CONTRACT_FILTERABLE_FIELDS.UPDATED_AT, 'Updated Date', 'date'),
  createBooleanField(CONTRACTS_COLUMNS_KEYS.AUTO_RENEWAL, 'Auto Renewal'),
  createFilterField(CONTRACTS_COLUMNS_KEYS.SIGNATURE_DATE, 'Signature Date', 'date'),
  createEnumSelectField(CONTRACTS_COLUMNS_KEYS.SYNC_STATUS, 'SO Sync', SYNC_STATUSES),
  createEnumSelectField(CONTRACTS_COLUMNS_KEYS.CURRENCY, 'Currency', CURRENCIES),
  createFilterField('daysToEnd', 'Days to End', 'number'),
  createFilterField('contractEstimation', 'Contract Estimation', 'number'),
  createFilterField(CONTRACTS_COLUMNS_KEYS.PLAN_DURATION, 'Duration (months)', 'number'),
];
